import { regionOfOrg, RegionId } from "./regionMap";

/**
 * The organizations a CORP support user can act as, and the support user that scopes SQL
 * Row-Level Security to each one.
 *
 * This table used to live inside OrgChangePage. It moved here because there are now TWO
 * readers — the org switcher (an explicit choice) and PrivateRoute (restoring the user's
 * last active tenant at login) — and they must agree exactly. A code that isn't in this
 * table is not a support target: `supportOrgFor` returns undefined for it and the caller
 * declines to impersonate, so a stale or tampered saved value cannot invent an ambient
 * email of its own.
 *
 * The `support` value is the literal ambient email sent as `X-Ambient-Email`. It is
 * enumerated rather than derived from the code so that adding an org is a deliberate act;
 * the backend mirrors the SUPPORT_<ORG>USER convention in SupportTenantPolicy.SupportUserFor
 * for logging only, and never authorizes an override from it.
 */
export interface SupportOrg {
  /** WHOTO / org code shown in the header and used for RLS impersonation. */
  code: string;
  label: string;
  /** Support user whose WHOTO scopes RLS to this org (SUPPORT_<ORG>USER convention). */
  support: string;
}

/**
 * Impersonation targets. The region each org lives in comes from the single source of
 * truth (regionMap.ts): CORP/CORP2/GEORG/VENTR are all in the AMERICA DB, while PJAYA is
 * in the ASIA DB and LYON in the EUROPE DB. With one SWA + multiple FAs, switching to
 * PJAYA or LYON just points the API layer at that region's Function App
 * (setActiveRegion) — no page/origin hop.
 */
export const SUPPORT_ORGS: SupportOrg[] = [
  { code: "CORP",  label: "CORP - QA Swagelok IT5",         support: "SUPPORT_CORPUSER"  },
  { code: "CORP2", label: "CORP2 - QA DNMRK",               support: "SUPPORT_CORP2USER" },
  { code: "GEORG", label: "GEORG - Georgia",                support: "SUPPORT_GEORGUSER" },
  { code: "VENTR", label: "VENTR - Southern California",    support: "SUPPORT_VENTRUSER" },
  { code: "PJAYA", label: "PJAYA - Petaling Jaya (Asia)",   support: "SUPPORT_PJAYAUSER" },
  { code: "LYON",  label: "LYON - Lyon (Europe)",           support: "SUPPORT_LYONUSER"  },
];

/** The org the switcher starts on. */
export const DEFAULT_SUPPORT_ORG = "CORP";

/** Canonical form for an org/WHOTO code lookup (trim + upper-case). */
export function canonicalOrgCode(value?: string | null): string {
  return value?.trim().toUpperCase() ?? "";
}

/**
 * The support-org entry for a code, or undefined when the code is unknown. Tolerant of
 * casing/whitespace on the way in (a saved value round-trips through SQL and JSON), strict
 * about membership.
 */
export function supportOrgFor(code?: string | null): SupportOrg | undefined {
  const canonical = canonicalOrgCode(code);
  if (!canonical) return undefined;
  return SUPPORT_ORGS.find((org) => org.code === canonical);
}

/** The ambient email that scopes RLS to an org, or undefined for an unknown org. */
export function supportUserFor(code?: string | null): string | undefined {
  return supportOrgFor(code)?.support;
}

/** The region whose Function App + database hosts an org (undefined if unmapped). */
export function regionOfSupportOrg(code?: string | null): RegionId | undefined {
  return regionOfOrg(canonicalOrgCode(code));
}
